import { useState, useCallback } from 'react';
import { Box, Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import { Psychology, Refresh } from '@mui/icons-material';
import { enhancedApi as jotcoachApi } from '@store/generated/jotcoach';
import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { JotCoachMetricCard } from '../components/jotcoach-metric-card';
import { JotCoachDateRangePicker } from '../components/jotcoach-date-range-picker';
import { JotCoachOrgLeaderboard } from '../components/jotcoach-org-leaderboard';
import { JotCoachUserActivityTable } from '../components/jotcoach-user-activity-table';

const {
  useGetV2AdminJotcoachSessionsAnalyticsDashboardQuery,
  useGetV2AdminJotcoachSessionsAnalyticsOrganizationsQuery,
  useGetV2AdminJotcoachSessionsAnalyticsUsersQuery,
} = jotcoachApi;

export default function JotCoachDashboard() {
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Fetch analytics data - only include dates if they're set
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useGetV2AdminJotcoachSessionsAnalyticsDashboardQuery({
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  });

  const {
    data: orgData,
    isLoading: isOrgLoading,
    error: orgError,
    refetch: refetchOrgs,
  } = useGetV2AdminJotcoachSessionsAnalyticsOrganizationsQuery({
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  });

  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError,
    refetch: refetchUsers,
  } = useGetV2AdminJotcoachSessionsAnalyticsUsersQuery({
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  });

  const handleDateRangeChange = useCallback((start: string | null, end: string | null) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleRefreshAll = useCallback(() => {
    void refetchDashboard();
    void refetchOrgs();
    void refetchUsers();
  }, [refetchDashboard, refetchOrgs, refetchUsers]);

  const getDateRangeLabel = () => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate).toLocaleDateString();
    const end = new Date(endDate).toLocaleDateString();
    return `${start} - ${end}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <Psychology fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                JotCoach Admin Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Global overview of JotCoach coaching sessions and engagement
              </Typography>
            </Box>
          </Box>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefreshAll}
            variant="outlined"
            disabled={isDashboardLoading || isOrgLoading || isUserLoading}
          >
            Refresh All
          </Button>
        </Box>

        {/* Date Range Picker */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <JotCoachDateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              defaultPreset="7days"
            />
            {startDate && endDate && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Selected Range: {getDateRangeLabel()}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Global Metrics Section */}
        <Box mb={6}>
          <Typography variant="h5" fontWeight="medium" mb={3}>
            Global Metrics
          </Typography>

          {dashboardError ? (
            <Card>
              <CardContent>
                <Typography color="error" align="center">
                  Failed to load dashboard metrics
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button onClick={() => void refetchDashboard()} variant="outlined" size="small">
                    Retry
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <JotCoachMetricCard
                  title="Total Sessions"
                  value={dashboardData?.total_sessions || 0}
                  subtitle={getDateRangeLabel() || 'Last 7 days'}
                  loading={isDashboardLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <JotCoachMetricCard
                  title="Active Organizations"
                  value={dashboardData?.unique_organizations || 0}
                  subtitle="Using JotCoach"
                  loading={isDashboardLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <JotCoachMetricCard
                  title="Active Providers"
                  value={dashboardData?.unique_users || 0}
                  subtitle="Using JotCoach"
                  loading={isDashboardLoading}
                  format="number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <JotCoachMetricCard
                  title="SMS Send Rate"
                  value={dashboardData?.sms_rate || 0}
                  subtitle={`${dashboardData?.sessions_with_sms || 0} / ${dashboardData?.total_sessions || 0} sessions`}
                  loading={isDashboardLoading}
                  format="percentage"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <JotCoachMetricCard
                  title="Feedback Response Rate"
                  value={dashboardData?.feedback_response_rate || 0}
                  subtitle={`${dashboardData?.sessions_with_feedback || 0} / ${dashboardData?.sessions_with_sms || 0} SMS sent`}
                  loading={isDashboardLoading}
                  format="percentage"
                />
              </Grid>
            </Grid>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Organization Leaderboard Section */}
        <Box mb={6}>
          {orgError ? (
            <Card>
              <CardContent>
                <Typography color="error" align="center">
                  Failed to load organization leaderboard
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button onClick={() => void refetchOrgs()} variant="outlined" size="small">
                    Retry
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <JotCoachOrgLeaderboard
              organizations={orgData?.organizations || []}
              loading={isOrgLoading}
            />
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* User Activity Section */}
        <Box mb={6}>
          {userError ? (
            <Card>
              <CardContent>
                <Typography color="error" align="center">
                  Failed to load user activity report
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button onClick={() => void refetchUsers()} variant="outlined" size="small">
                    Retry
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <JotCoachUserActivityTable users={userData?.users || []} loading={isUserLoading} />
          )}
        </Box>
      </main>
    </div>
  );
}
